/**
 * Suppliers, their prices and orders — STORY-171 (SU-D1–SU-D16).
 *
 * **The rules that decide "who is cheapest" live here, once**, so the server's
 * refusal of a reasonless dearer order and the screen's "Cheapest" chip cannot
 * disagree. Every function is pure; nothing here is clinic data.
 *
 * Money is in minor units of the clinic's currency throughout, never floats.
 */

/* ── The link from a price to an item (SU-D14) ──────────────────────────── */

/**
 * An item name reduced to what identifies it: lower case, spacing collapsed.
 *
 * **A price reaches an item by this key, at every location** — a supplier's
 * "Item  One" is the price of the item called "item one" wherever it
 * is held. Punctuation is kept on purpose: "Gloves, medium" and "Gloves
 * medium" may be two different things, and guessing would be worse than
 * asking.
 */
export function itemKey(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLowerCase();
}

/* ── Comparing prices (SU-D1) ───────────────────────────────────────────── */

/** The two numbers a per-use comparison needs. */
export interface PerUsePrice {
  priceCents: number;
  usesPerPack: number;
}

/**
 * Negative when `a` is cheaper per use, zero when the same, positive when dearer.
 *
 * **Cross-multiplied in whole minor units, never divided**, so no rounding can
 * flip a winner or split a tie: 2450/10 against 5200/20 is 2450×20 against
 * 5200×10. Both stay far inside `Number.MAX_SAFE_INTEGER` at the validation
 * bounds (ten million minor units × a hundred thousand uses).
 */
export function comparePerUse(a: PerUsePrice, b: PerUsePrice): number {
  return a.priceCents * b.usesPerPack - b.priceCents * a.usesPerPack;
}

/**
 * Cheapest per use first; **a tie keeps no winner** (SU-D1) — the supplier
 * name orders it only so the list does not shuffle between reads.
 */
export function rankByUse<T extends PerUsePrice & { supplierName: string }>(
  prices: readonly T[],
): T[] {
  return [...prices].sort(
    (a, b) => comparePerUse(a, b) || a.supplierName.localeCompare(b.supplierName),
  );
}

/**
 * Every price tied for cheapest — **one, several, or none for an empty list.**
 *
 * Several is a real answer, not an edge case to resolve: Anthony's rule is to
 * say they are the same and let the assistant pick (SU-D1).
 */
export function cheapestOf<T extends PerUsePrice>(prices: readonly T[]): T[] {
  if (prices.length === 0) return [];
  const best = prices.reduce((min, p) => (comparePerUse(p, min) < 0 ? p : min));
  return prices.filter((p) => comparePerUse(p, best) === 0);
}

/* ── How much to order (SU-D5) ──────────────────────────────────────────── */

/**
 * What "a full shelf" is for an item: its par level, or one stock unit when the
 * clinic has not said — the same line STORY-112's notice is measured against.
 */
export function fullShelf(item: { parLevel: number | null; usesPerStockUnit: number }): number {
  return item.parLevel ?? item.usesPerStockUnit;
}

/**
 * Enough whole packs to bring the shelf back to full, **and never fewer than
 * one** — a suggestion of zero is not an order.
 *
 * A negative count (allowed, TP14) simply asks for more.
 */
export function suggestPacks(
  item: {
    quantityOnHand: number;
    parLevel: number | null;
    usesPerStockUnit: number;
    kind?: "COUNTABLE" | "BULK" | "REUSABLE";
  },
  usesPerPack: number,
): number {
  const missing = fullShelf(item) - item.quantityOnHand;
  // **A bulk item is already counted in packs** (FX22, IA-D6): what is missing is
  // the number of packs, whatever a supplier says one of theirs holds — dividing
  // it again by their uses per pack suggested one box for an empty shelf.
  if (item.kind === "BULK") return Math.max(1, Math.ceil(missing));
  return Math.max(1, Math.ceil(missing / usesPerPack));
}

/** The quick picks for ordering from a dearer supplier (SU-D12). Free text is also accepted. */
export const DEARER_REASONS = [
  "The cheapest is out of stock",
  "Needed sooner",
  "Better quality",
] as const;

/* ── Shapes on the wire ─────────────────────────────────────────────────── */

/**
 * One row of the catalogue (STORY-189b, MOD-D22): an item that at least one of
 * the clinic's suppliers sells, **with every supplier's price for it**, ranked
 * cheapest per use first. Several suppliers selling one item is one row.
 */
export interface CatalogueEntry {
  itemKey: string;
  /** As the first supplier spelt it. */
  itemName: string;
  offers: SupplierPrice[];
  /** Every offer tied for cheapest per use — more than one is "the same price" (SU-D1). */
  cheapestIds: string[];
  /** This location's stock item of that name, or null when it is not held here yet. */
  inStock: {
    itemId: string;
    quantityOnHand: number;
    isLow: boolean;
    /** An order for it is already on its way, so it cannot go in the cart (MOD-D24). */
    onOrder: boolean;
    /** Marked to buy (WB-D1): the row is starred and sorts near the top. */
    wantToBuy: boolean;
  } | null;
}

/** Add a catalogue item to this location's stock, from one supplier's price line. */
export interface AddCatalogueToStock {
  priceId: string;
  /** Defaults to COUNTABLE; the clinic can change it on the item. */
  kind?: "COUNTABLE" | "BULK" | "REUSABLE";
  /**
   * The item's own price, in **this location's** currency (IA-D9). Required when
   * that differs from the clinic's, which is the currency supplier prices are in
   * (QA4-D11); otherwise the supplier's price is copied as before.
   */
  priceCents?: number;
}

export interface SupplierPrice {
  id: string;
  supplierId: string;
  supplierName: string;
  itemName: string;
  itemKey: string;
  pack: string;
  usesPerPack: number;
  priceCents: number;
  /** Null when the supplier has not said. */
  leadDays: number | null;
  /** ISO 8601. */
  updatedAt: string;
}

export interface Supplier {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  note: string | null;
  isActive: boolean;
  /** Their whole list, by item name. */
  prices: SupplierPrice[];
}

export interface SaveSupplier {
  name: string;
  phone?: string | null;
  email?: string | null;
  note?: string | null;
  isActive?: boolean;
}

/**
 * Add an item to a supplier's list, or change its price (`PATCH`).
 *
 * `createItem` adds the item to **this location's** inventory in the same
 * transaction, for a name nothing in the clinic holds yet (SU-D11).
 */
export interface SaveSupplierPrice {
  itemName: string;
  pack: string;
  usesPerPack: number;
  priceCents: number;
  leadDays?: number | null;
  createItem?: {
    kind: "COUNTABLE" | "BULK" | "REUSABLE";
    /** In uses. Omitted → one pack counts as a full shelf (STORY-112), SU-D11. */
    parLevel?: number;
    /** Required when `kind` is bulk, refused otherwise (MC-D4). */
    estimatedUses?: number;
  };
}

export type SupplierOrderStatus = "OPEN" | "PARTIALLY_RECEIVED" | "RECEIVED" | "CANCELLED";

/**
 * **Still on its way** (IA-D3): ordered and not here, or part of it here and the
 * rest still coming. Read wherever "on order" is asked, so a part-delivered order
 * blocks a second one, can be received again and can be cancelled.
 */
export const ON_ITS_WAY: readonly SupplierOrderStatus[] = ["OPEN", "PARTIALLY_RECEIVED"];

export function isOnItsWay(status: SupplierOrderStatus): boolean {
  return ON_ITS_WAY.includes(status);
}

/**
 * What became of "save it as the supplier's price" on a receipt (IA-D4, IA-D10).
 * `NOT_ALLOWED` is somebody without `inventory.items.manage`; `PACK_CHANGED` is a
 * price line whose pack is no longer the one ordered, so the invoice price does
 * not describe it; `NO_LINE` is a line since removed.
 */
export type ReceivePriceOutcome =
  "UNCHANGED" | "UPDATED" | "NOT_ASKED" | "NOT_ALLOWED" | "PACK_CHANGED" | "NO_LINE";

export interface SupplierOrder {
  id: string;
  branchId: string;
  inventoryItemId: string;
  itemName: string;
  /** The item's kind: a bulk item's packs arrive as packs, not uses (FX22). */
  itemKind: "COUNTABLE" | "BULK" | "REUSABLE";
  supplierId: string;
  supplierName: string;
  status: SupplierOrderStatus;
  pack: string;
  usesPerPack: number;
  /** Per pack, as ordered. */
  priceCents: number;
  packs: number;
  leadDays: number | null;
  /** The checkout this line was placed in, or null for one placed on its own (STORY-189c). */
  batchId: string | null;
  /** Why not the cheapest (SU-D7). */
  reason: string | null;
  orderedByName: string | null;
  orderedAt: string;
  receivedPacks: number | null;
  receivedPriceCents: number | null;
  receivedByName: string | null;
  receivedAt: string | null;
  cancelledAt: string | null;
  /** Who cancelled it (IA-D8); null when not cancelled, or once they left. */
  cancelledByName: string | null;
  /** Set on the response to a receipt only: what happened to the supplier's price. */
  priceOutcome?: ReceivePriceOutcome;
  /**
   * When somebody messaged the supplier (SU-D18); null is "Not sent yet". The
   * expected arrival counts from here (SU-D19), never from `orderedAt`.
   */
  sentAt: string | null;
  sentByName: string | null;
}

/**
 * One line of a location's cart (SU-D17) — **one cart per location, shared by
 * everyone ordering there**, kept on the server. One line per stock item; the
 * supplier is the price line it came from. `cheapest` is worked out when read,
 * so a price change elsewhere is reflected the next time anybody looks.
 */
export interface SupplyCartLine {
  inventoryItemId: string;
  itemName: string;
  supplierPriceId: string;
  supplierId: string;
  supplierName: string;
  pack: string;
  usesPerPack: number;
  priceCents: number;
  cheapest: boolean;
  packs: number;
  reason: string;
  addedByName: string | null;
}

/** Put an item in the cart, or change its line. Omitted fields keep their value. */
export interface SaveSupplyCartLine {
  supplierPriceId?: string;
  packs?: number;
  reason?: string;
}

export interface CreateSupplierOrder {
  inventoryItemId: string;
  supplierPriceId: string;
  packs: number;
  /** Required when the price is not among the cheapest (SU-D7). */
  reason?: string;
}

export interface ReceiveSupplierOrder {
  packs: number;
  /** Per pack, from the invoice. Omitted means as ordered. */
  priceCents?: number;
  /** Save an invoice price that differs as the supplier's new price (SU-D6). */
  updatePrice: boolean;
  /** Fewer than ordered and the rest is still coming (IA-D3): the order stays on its way. */
  stillComing?: boolean;
}

/**
 * One item that is running low at this location, with what to do about it —
 * what Home's notice and the popup draw (SU-D3).
 */
export interface ReorderSuggestion {
  /** The open `INVENTORY_LOW` notice — the popup shows each one once. */
  alertId: string;
  itemId: string;
  itemName: string;
  quantityOnHand: number;
  fullShelf: number;
  /** Cheapest per use first. Empty when no supplier sells it. */
  prices: (SupplierPrice & { supplierPhone: string | null; suggestedPacks: number })[];
  /** Ids of every price tied for cheapest (SU-D1). */
  cheapestIds: string[];
  /** Set while an order is on the way; the notice then goes quiet. */
  openOrder: SupplierOrder | null;
}

/* ── Importing a supplier's list (SU-D10, SU-D13) ───────────────────────── */

/** One row as read from the file, every cell still text. `row` is the sheet's row number. */
export interface SupplierImportRow {
  row: number;
  item: string;
  pack: string;
  uses: string;
  price: string;
  days: string;
}

/**
 * What to do with a name that matches no item anywhere in the clinic:
 * create it at this location, treat it as an existing item, or skip it.
 */
export type SupplierImportChoice =
  { kind: "create" } | { kind: "match"; itemName: string } | { kind: "skip" };

export type SupplierImportOutcome =
  "add" | "change" | "same" | "unknown" | "create" | "skip" | "duplicate" | "error";

export interface SupplierImportPlanRow {
  row: number;
  itemName: string;
  pack: string;
  usesPerPack: number | null;
  priceCents: number | null;
  leadDays: number | null;
  outcome: SupplierImportOutcome;
  /** Why it was skipped or refused, in words. */
  note: string | null;
  /** On a change: what it was. */
  previous: {
    pack: string;
    usesPerPack: number;
    priceCents: number;
    leadDays: number | null;
  } | null;
  /** On an unknown name: an existing item that shares a word with it. */
  suggestion: string | null;
}

export interface SupplierImportPreview {
  rows: SupplierImportPlanRow[];
  /** Rows that still need a choice before the import may run. */
  undecided: number;
  /** Rows the import would write. */
  writes: number;
}

export interface SupplierImportRequest {
  rows: SupplierImportRow[];
  /** Keyed by `row`. */
  choices?: Record<string, SupplierImportChoice>;
}

export interface SupplierImportResult {
  added: number;
  changed: number;
  created: number;
  untouched: number;
}

/* ── The cart and its checkout (STORY-189c, MOD-D23, MOD-D24) ───────────── */

/** One line of a checkout: the item, the price line it is bought on, and how many packs. */
export interface CheckoutLine {
  inventoryItemId: string;
  supplierPriceId: string;
  packs: number;
  /** Required when the price is not among the cheapest for the item (SU-D7). */
  reason?: string;
}

/**
 * One supplier's order out of a checkout: every line asked for from them, with
 * what to say and who to say it to. **DentaCore never sends it** (SU-D4).
 */
export interface SupplierOrderBatch {
  id: string;
  supplierId: string;
  supplierName: string;
  supplierPhone: string | null;
  orderedAt: string;
  orders: SupplierOrder[];
  /** Packs × price per pack across the lines, in minor units. */
  totalCents: number;
}
